import type { Notification } from "../../services/communicationService";
import { byText, reversed, type RegisterConfig } from "../register/registerLogic";

/** What a notification is about, read from its type and where it links. */
export function notificationKind(notification: Pick<Notification, "notification_type" | "url">) {
  const url = notification.url ?? "";
  if (notification.notification_type === "message" || /^\/messages\//.test(url) || /[?&]message=/.test(url)) return "Message";
  if (notification.notification_type === "announcement" || /^\/announcements\//.test(url) || /[?&]announcement=/.test(url)) return "Announcement";
  if (/^\/(financial|staff|my-approvals)(\/|\?|$)/.test(url)) return "Approval";
  if (/^\/appraisals/.test(url)) return "Appraisal";
  return "Alert";
}

/** A dot for how the news went: approved, returned, rejected, or plain information. */
export function notificationTone(notification: Pick<Notification, "notification_type">) {
  switch (notification.notification_type) {
    case "success":
      return "bg-emerald-500";
    case "warning":
      return "bg-amber-500";
    case "error":
      return "bg-red-500";
    default:
      return "bg-sky-500";
  }
}

/**
 * Where a notification leads, as a route in this app. Older message and
 * announcement notifications point at /messages/12/ and /announcements/5/,
 * which never existed as pages; they open the item on the Communication page.
 */
export function notificationLink(url: string | null | undefined) {
  const path = (url ?? "").trim();
  const message = path.match(/^\/messages\/(\d+)\/?$/);
  if (message) return { to: `/communication?tab=messages&message=${message[1]}`, label: "Open the message" };
  const announcement = path.match(/^\/announcements\/(\d+)\/?$/);
  if (announcement) return { to: `/communication?tab=announcements&announcement=${announcement[1]}`, label: "Open the announcement" };
  if (/[?&]message=\d+/.test(path)) return { to: path, label: "Open the message" };
  if (/[?&]announcement=\d+/.test(path)) return { to: path, label: "Open the announcement" };
  // Only this app's own pages; anything else is left unlinked.
  if (path.startsWith("/") && !path.startsWith("//")) return { to: path, label: "Go to the page" };
  return null;
}

export const notificationConfig: RegisterConfig<Notification> = {
  search: (notification) => [notification.title, notification.message, notificationKind(notification)].join(" "),
  views: [
    { key: "unread", label: "Unread", match: (notification) => !notification.read_at },
    { key: "all", label: "All" },
    { key: "read", label: "Read", match: (notification) => !!notification.read_at },
  ],
  date: { label: "Received", value: (notification) => notification.created_at },
  category: { label: "About", value: notificationKind },
  sorts: [
    { key: "-at", label: "Newest first", compare: reversed(byText<Notification>((notification) => notification.created_at)) },
    { key: "at", label: "Oldest first", compare: byText<Notification>((notification) => notification.created_at) },
  ],
};
